import { natsClient } from './NATSClient';
import { orgNatsClient, StatementIds } from './OrgNATSClient';

// Type definitions for backend data matching SAP structure
export interface Machine {
  maschinennummer: string;           // Machine number from SAP
  bautyp: string;                   // Machine type/model (e.g., "Modell 349")
  maschineBK: string;               // Machine BK identifier
  datenVerfuegbar: boolean;         // Data availability flag
  cmVerfuegbar?: boolean;           // CM (Condition Monitoring) data availability flag
  // Additional fields for our dashboard
  machineId?: string;               // Simplified machine ID for UI
  machineType?: string;             // Simplified machine type for UI
  machineName?: string;             // Display name
  status?: 'running' | 'stopped' | 'maintenance' | 'error';
  lineId?: string;                  // Reference to parent line
  factoryName?: string;             // Reference to factory
  customerName?: string;            // Customer name
  // Flow layout fields (for machine chain visualization)
  flowOrder?: number;               // Position order in the flow chain (0-based)
  flowGroup?: number;               // Group index for parallel machines (same group = parallel)
  // API fields for ordering (from backend)
  order_num?: number;               // Order number from backend
  order_line?: number;              // Order line/position from backend
  // Legacy compatibility fields
  id?: string;                      // For legacy data compatibility
  type?: string;                    // For legacy data compatibility
}

export interface Line {
  linieName: string;                // Line name from SAP
  linieBK: string;                  // Line BK identifier  
  linieNummer: string;              // Line number (can be "undefined")
  oemNummer?: string;               // OEM number for the line
  maschinen: Machine[];             // Array of machines in this line
  // Additional fields for our dashboard
  lineId?: string;                  // Simplified ID
  factoryName?: string;             // Reference to factory
  supervisor?: string;              // Line supervisor
  status?: 'active' | 'inactive' | 'maintenance';
  totalMachineCount?: number;       // Unfiltered total machine count for treeview
}

export interface Factory {
  werkName: string;                 // Factory name from SAP
  linien: Line[];                   // Array of lines in this factory
  // Additional fields for our dashboard
  factoryId?: string;               // Simplified ID
  location?: string;                // Factory location
  customerName?: string;            // Customer this factory belongs to
  region?: string;                  // Geographic region
  country?: string;                 // Country
  lineCount?: number;               // Calculated count
  machineCount?: number;            // Calculated count
  status?: 'operational' | 'maintenance' | 'inactive';
}

export interface Customer {
  kundennummer: string;             // Customer number from SAP
  kundenName: string;               // Customer name from SAP
  werke: Factory[];                 // Array of factories for this customer
}

export interface Region {
  region: string;                   // Region name
  laender: Country[];               // Array of countries in this region
}

export interface Country {
  land: string;                     // Country name
  kunden: Customer[];               // Array of customers in this country
}

export interface CustomerGroup {
  kundengruppe: string;             // Customer group name
  regionen: Region[];               // Array of regions in this group
}

// Backend data service that includes Keycloak authentication
export class BackendDataService {
  private static readonly REQUEST_TIMEOUT = 10000; // 10 seconds

  /**
   * Fetch ALL machines (for tree view, loaded once)
   * Uses org-scoped NATS request: {org-alias}.data.request with PC.available_machines
   */
  static async fetchSAPData(token: string | null): Promise<Record<string, CustomerGroup>> {
    if (!natsClient.isConnected()) {
      console.warn('BackendDataService.fetchSAPData: NATS not connected');
      return {};
    }

    if (!token) {
      console.warn('BackendDataService.fetchSAPData: No token provided');
      return {};
    }

    try {
      const response = await orgNatsClient.request(
        StatementIds.AVAILABLE_MACHINES,
        {
          kundengruppeGeschaeftsfeld: 'Tabak'
        },
        token,
        { timeout: 15000 }
      );

      // Check if response was successful
      if (response.success && response.data) {
        const data = response.data as Record<string, CustomerGroup>;
        return data;
      } 
      else {
        console.error('BackendDataService: Backend error:', response.error);
        return {};
      }
    } catch (error) {
      console.error('BackendDataService: Failed to fetch data:', error);
      return {};
    }
  }

  /**
   * Fetch ONLY machines with data (datenVerfuegbar=1, ~357 machines)
   * Uses org-scoped NATS request: {org-alias}.data.request with PC.machines_with_data
   * This is a smaller dataset for faster display when filtering by data availability
   */
  static async fetchMachinesWithData(token: string | null): Promise<Record<string, CustomerGroup>> {
    if (!natsClient.isConnected()) {
      return {};
    }

    if (!token) {
      return {};
    }

    try {
      const response = await orgNatsClient.request(
        StatementIds.MACHINES_WITH_DATA,
        {
          kundengruppeGeschaeftsfeld: 'Tabak'
        },
        token,
        { timeout: 15000 }
      );

      // Check if response was successful
      if (response.success && response.data) {
        return response.data as Record<string, CustomerGroup>;
      } 
      else {
        console.error('BackendDataService: Backend error (machines with data):', response.error);
        return {};
      }
    } catch (error) {
      console.error('BackendDataService: Failed to fetch machines with data:', error);
      return {};
    }
  }

  /**
   * Transform SAP data to extract machines list with flattened structure
   */
  static transformSAPDataToMachines(sapData: Record<string, CustomerGroup>): Machine[] {
    const machines: Machine[] = [];

    Object.values(sapData).forEach(customerGroup => {
      customerGroup.regionen.forEach(region => {
        region.laender.forEach(country => {
          country.kunden.forEach(customer => {
            customer.werke.forEach(factory => {
              factory.linien.forEach(line => {
                line.maschinen.forEach(machine => {
                  const transformedMachine: Machine = {
                    ...machine,
                    machineId: machine.maschinennummer || machine.id, // Map to machineId
                    machineType: machine.bautyp || machine.type, // Map to machineType
                    machineName: `${machine.bautyp || machine.type} (${machine.maschinennummer || machine.id})`,
                    lineId: line.linieBK,
                    factoryName: factory.werkName,
                    customerName: customer.kundenName,
                    status: machine.datenVerfuegbar ? 'running' : 'stopped',
                    // Map backend flow-order fields. The SAP payload uses camelCase
                    // (orderNum/orderLine); orderNum may be missing for some
                    // machines — fall back to orderLine then index.
                    flowOrder:
                      (machine as any).orderNum ??
                      (machine as any).order_num ??
                      machine.flowOrder ??
                      0,
                    flowGroup:
                      (machine as any).orderLine ??
                      (machine as any).order_line ??
                      machine.flowGroup,
                  };
                  machines.push(transformedMachine);
                });
              });
            });
          });
        });
      });
    });

    return machines;
  }

  /**
   * Transform SAP data to extract lines list with flattened structure
   */
  static transformSAPDataToLines(sapData: Record<string, CustomerGroup>): Line[] {
    const lines: Line[] = [];

    Object.values(sapData).forEach(customerGroup => {
      customerGroup.regionen.forEach(region => {
        region.laender.forEach(country => {
          country.kunden.forEach(customer => {
            customer.werke.forEach(factory => {
              factory.linien.forEach(line => {
                // Use preserved totalMachineCount if available, otherwise calculate from current maschinen array
                const originalMachineCount = line.totalMachineCount ?? (Array.isArray(line.maschinen) ? line.maschinen.length : 0);

                // Map SAP flow-order fields onto each machine. SAP delivers these
                // as camelCase (orderNum/orderLine); snake_case is kept as a
                // fallback. Without this the line flow chain (which renders
                // line.maschinen directly) has no flowOrder and falls back to
                // array index, ignoring the SAP-defined order.
                const maschinen = (line.maschinen || []).map((machine, idx) => {
                  // SAP delivers sort-order as camelCase orderNum (= DB order_num).
                  // If the backend omits it, treat as 0 (first position).
                  const rawOrder =
                    (machine as any).orderNum ??
                    (machine as any).order_num ??
                    machine.flowOrder ??
                    0;
                  const rawGroup =
                    (machine as any).orderLine ??
                    (machine as any).order_line ??
                    machine.flowGroup ??
                    idx;
                  return {
                    ...machine,
                    flowOrder: rawOrder,
                    flowGroup: rawGroup,
                  };
                });

                lines.push({
                  ...line,
                  maschinen,
                  lineId: line.linieBK,
                  factoryName: factory.werkName,
                  status: maschinen.some(m => m.datenVerfuegbar) ? 'active' : 'inactive',
                  totalMachineCount: originalMachineCount,
                });
              });
            });
          });
        });
      });
    });

    return lines;
  }

  /**
   * Transform SAP data to extract factories list with calculated metrics
   */
  static transformSAPDataToFactories(sapData: Record<string, CustomerGroup>): Factory[] {
    const factories: Factory[] = [];

    Object.values(sapData).forEach(customerGroup => {
      customerGroup.regionen.forEach(region => {
        region.laender.forEach(country => {
          country.kunden.forEach(customer => {
            customer.werke.forEach(factory => {
              const lineCount = factory.linien.length;
              const machineCount = factory.linien.reduce((sum, line) => sum + line.maschinen.length, 0);
              const hasDataAvailable = factory.linien.some(line => 
                line.maschinen.some(machine => machine.datenVerfuegbar)
              );

              factories.push({
                ...factory,
                factoryId: factory.werkName.replace(/\s+/g, '_').toLowerCase(),
                location: country.land,
                customerName: customer.kundenName,
                region: region.region,
                country: country.land,
                lineCount,
                machineCount,
                status: hasDataAvailable ? 'operational' : 'inactive'
              });
            });
          });
        });
      });
    });

    return factories;
  }

  /**
   * Fetch machines that the current user is allowed to see
   */
  static async fetchMachines(token: string | null): Promise<Machine[]> {
    const sapData = await this.fetchSAPData(token);
    return this.transformSAPDataToMachines(sapData);
  }

  /**
   * Fetch lines that the current user is allowed to see
   */
  static async fetchLines(token: string | null): Promise<Line[]> {
    const sapData = await this.fetchSAPData(token);
    return this.transformSAPDataToLines(sapData);
  }

  /**
   * Fetch factories that the current user is allowed to see
   */
  static async fetchFactories(token: string | null): Promise<Factory[]> {
    const sapData = await this.fetchSAPData(token);
    return this.transformSAPDataToFactories(sapData);
  }

  /**
   * Fetch machine details by ID
   */
  static async fetchMachineDetails(machineId: string, token: string | null): Promise<Machine | null> {
    if (!natsClient.isConnected() || !token) {
      return null;
    }

    try {
      const requestPayload = {
        machineId,
        token,
        timestamp: new Date().toISOString(),
        requestId: `machine_details_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      const response = await this.makeNATSRequest(
        'backend.machines.details',
        requestPayload
      );

      if (response.success && response.data) {
        return response.data as Machine;
      } else {
        console.error('Backend returned error:', response.error);
        return null;
      }
    } catch (error) {
      console.error(`Failed to fetch machine details for ${machineId}:`, error);
      return null;
    }
  }

  /**
   * Fetch line details by ID
   */
  static async fetchLineDetails(lineId: string, token: string | null): Promise<Line | null> {
    if (!natsClient.isConnected() || !token) {
      return null;
    }

    try {
      const requestPayload = {
        lineId,
        token,
        timestamp: new Date().toISOString(),
        requestId: `line_details_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      const response = await this.makeNATSRequest(
        'backend.lines.details',
        requestPayload
      );

      if (response.success && response.data) {
        return response.data as Line;
      } else {
        console.error('Backend returned error:', response.error);
        return null;
      }
    } catch (error) {
      console.error(`Failed to fetch line details for ${lineId}:`, error);
      return null;
    }
  }

  /**
   * Fetch available dates for a specific machine
   */
  static async fetchAvailableDates(
    serialno: string, 
    dateFrom: string, 
    dateTo: string
  ): Promise<string[]> {
    if (!natsClient.isConnected()) {
      return [];
    }

    try {
      const requestPayload = {
        serialno,
        date_from: dateFrom,
        date_to: dateTo
      };

      const response = await this.makeNATSRequest(
        'pc.available_dates',
        requestPayload
      );

      if (response && response.status === 'success') {
        // Backend returns array of {date, exist_SAP, machine_sn} - extract just dates
        const results = Array.isArray(response.results) ? response.results : (response.results?.available_dates || []);
        return results.map((item: { date: string }) => item.date.split('T')[0]); // Extract date part
      } else {
        return [];
      }
    } catch (error) {
      console.error('BackendDataService: Failed to fetch available dates:', error);
      return [];
    }
  }

  /**
   * Generic NATS request-response helper with retry logic
   */
  private static async makeNATSRequest(subject: string, payload: any, retries = 2): Promise<any> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Check if NATS is connected before making request
        if (!natsClient.isConnected()) {
          throw new Error('NATS connection lost');
        }
        
        const response = await natsClient.request(subject, payload, this.REQUEST_TIMEOUT);
        return response;
      } catch (error) {
        // If this is not the last attempt, wait and retry
        if (attempt < retries) {
          const waitTime = (attempt + 1) * 2000; // 2s, 4s, etc.
          await new Promise(resolve => setTimeout(resolve, waitTime));
          
          // Try to reconnect NATS if connection is lost
          if (!natsClient.isConnected()) {
            try {
              // Get current token from Keycloak if available
              const currentToken = (window as any).keycloak?.token;
              if (currentToken) {
                const { initializeNATS } = await import('./NATSClient');
                const natsUrl = import.meta.env.VITE_NATS_URL || 'wss://natsws.enlixy.com:443';
                await initializeNATS([natsUrl], currentToken);
              }
            } catch (reconnectError) {
              console.error('❌ BackendDataService: NATS reconnection failed:', reconnectError);
            }
          }
        } else {
          // Final attempt failed
          throw error;
        }
      }
    }
  }
}
